import {
  BookkeepingActionFilter as BookkeepingActionFilterProto,
  BookkeepingPendingAction as BookkeepingPendingActionProto,
  ConfirmManagedPlatformAccessRequest,
  GetBookkeepingPendingActionAggregationsRequest,
  ListBookkeepingPendingActionsRequest,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { collection } from 'infra/collection'
import BookkeepingPendingAction, {
  BookkeepingManagerAction,
} from './bookkeeping/BookkeepingPendingAction'
import { BookkeepingManagerAPI } from './bookkeeping/API'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import { Id } from 'types/definitions'
import { map } from 'rxjs'

export interface SearchBookkeepingPendingActionRequest {
  workspaceIds?: string[]
  orderBy?: string
  pageSize?: number
  pageToken?: string
  searchText?: string
  action?: BookkeepingManagerAction
}

export interface BookkeepingPendingActionAggregations {
  total: number
  confirmAccessTotal: number
}

export default class BookkeepingManagerStore {
  /**
   * Bookkeeping events from the realtime channel.
   */
  readonly events$ = this.bookkeepingManagerAPI.events$.pipe(
    map((x) => x.getBookkeepingUpdated()?.toObject().progressionInfo?.id),
  )

  /**
   * Bookkeeping pending actions collection.
   */
  bookkeepingPendingActions = collection<
    BookkeepingPendingActionProto.AsObject,
    BookkeepingPendingAction
  >({
    create: () => new BookkeepingPendingAction(this.primeMember),
  })

  constructor(
    private bookkeepingManagerAPI: BookkeepingManagerAPI,
    private primeMember: PrimeMemberByPublicId,
  ) {}

  /**
   * Searches bookkeeping pending actions.
   *
   * @param query
   * @returns
   */
  async searchBookkeepingPendingActions(
    query: SearchBookkeepingPendingActionRequest,
  ) {
    const request = new ListBookkeepingPendingActionsRequest()

    if (query.workspaceIds) {
      request.setWorkspaceIdsList(query.workspaceIds)
    }

    if (query.orderBy) {
      request.setOrderBy(query.orderBy)
    }

    if (query.pageSize) {
      request.setPageSize(query.pageSize)
    }

    if (query.pageToken) {
      request.setPageToken(query.pageToken)
    }

    switch (query.action) {
      case BookkeepingManagerAction.ConfirmAccess:
        request.setActionFilter(BookkeepingActionFilterProto.CONFIRM_ACCESS)
        break
      default:
        request.setActionFilter(BookkeepingActionFilterProto.ALL)
    }

    if (query.searchText) {
      request.setSearchText(query.searchText)
    }

    const result =
      await this.bookkeepingManagerAPI.listBookkeepingPendingActions(request)

    return {
      data: this.bookkeepingPendingActions.set(
        result.getBookkeepingPendingActionsList().map((op) => op.toObject()),
      ),
      total: result.getBookkeepingPendingActionsList().length,
      cursor: result.getNextPageToken(),
    }
  }

  /**
   * Gets bookkeeping pending action aggregations.
   *
   * @param workspaceIds
   * @returns
   */
  async getBookkeepingPendingActionAggregations(
    workspaceIds?: string[],
  ): Promise<BookkeepingPendingActionAggregations> {
    const request = new GetBookkeepingPendingActionAggregationsRequest()

    if (workspaceIds) {
      request.setWorkspaceIdsList(workspaceIds)
    }

    const resp = await this.bookkeepingManagerAPI
      .getBookkeepingPendingActionAggregations(request)
      .then((x) => x.toObject())

    return {
      total: resp.total,
      confirmAccessTotal: resp.confirmAccessTotal,
    }
  }

  /**
   * Confirms managed platform access.
   *
   * @returns
   */
  async confirmManagedPlatformAccess(id: Id): Promise<void> {
    const assertedId = Id.assert(id)
    const request = new ConfirmManagedPlatformAccessRequest().setId(assertedId)

    await this.bookkeepingManagerAPI.confirmManagedPlatformAccess(request)

    this.bookkeepingPendingActions.remove(assertedId)
  }
}
